import React, { useState, useEffect } from 'react';
import { schoolsAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

const AdminSchools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', pincode: '', grades_supported: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterZip, setFilterZip] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const load = () => schoolsAPI.getAll().then(r => setSchools(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => { setForm({ name: '', address: '', city: '', state: '', pincode: '', grades_supported: '' }); setEditing(null); setShowForm(true); setError(''); };
  const openEdit = (s) => { setForm(s); setEditing(s.id); setShowForm(true); setError(''); };

  const handleSave = async () => {
    if (!form.name) { setError('School name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await schoolsAPI.update(editing, form);
      else await schoolsAPI.create(form);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this school?')) return;
    await schoolsAPI.delete(id);
    load();
  };

  const uniqueStates = [...new Set(schools.map(s => s.state).filter(Boolean))].sort();
  const filteredSorted = schools
    .filter(s => {
      const q = search.toLowerCase();
      const matchSearch = !q || s.name.toLowerCase().includes(q) || (s.city || '').toLowerCase().includes(q);
      const matchState = !filterState || (s.state || '').toLowerCase() === filterState.toLowerCase();
      const matchZip = !filterZip || (s.pincode || '').startsWith(filterZip);
      return matchSearch && matchState && matchZip;
    })
    .sort((a, b) => {
      const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
      const n = parseFloat(va), m = parseFloat(vb);
      const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
      return sortDir === 'desc' ? -cmp : cmp;
    });
  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h1>Schools</h1><p>{schools.length} schools</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ Add School</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <input className="form-input" placeholder="Search name or city…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 180, maxWidth: 280 }} />
        <select className="form-select" style={{ minWidth: 150 }} value={filterState} onChange={e => setFilterState(e.target.value)}>
          <option value="">All States</option>
          {uniqueStates.map(st => <option key={st} value={st}>{st}</option>)}
        </select>
        <input className="form-input" placeholder="Zipcode" value={filterZip} onChange={e => setFilterZip(e.target.value)} style={{ width: 140 }} />
        {(search || filterState || filterZip) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setSearch(''); setFilterState(''); setFilterZip(''); }}>Clear</button>
        )}
        <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{filteredSorted.length} of {schools.length} schools</span>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #2563eb' }}>
          <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editing ? 'Edit School' : 'New School'}</h2>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">School Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} />
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={form.state} onChange={e => set('state', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Zipcode</label>
              <input className="form-input" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Grades (comma-separated)</label>
              <input className="form-input" value={form.grades_supported} onChange={e => set('grades_supported', e.target.value)} placeholder="1,2,3,4,5,6,7,8" />
            </div>
          </div>
          <div className="flex gap-2" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>School {si('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('city')}>City {si('city')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('state')}>State {si('state')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('pincode')}>Zipcode {si('pincode')}</th>
                <th>Grades</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('is_active')}>Status {si('is_active')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map(s => (
                <tr key={s.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{s.address}</div>
                  </td>
                  <td>{s.city || '—'}</td>
                  <td>{s.state || '—'}</td>
                  <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{s.pincode || '—'}</td>
                  <td style={{ fontSize: '0.8rem' }}>{s.grades_supported || '—'}</td>
                  <td><span className={`badge ${s.is_active ? 'badge-success' : 'badge-danger'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(s)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSorted.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>{schools.length === 0 ? 'No schools yet.' : 'No schools match the filters.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSchools;
